import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  Clock,
  CheckCircle2,
  Loader2,
  Package,
  User,
  Calendar,
  AlertTriangle,
  Hand,
  RefreshCw,
  Filter,
  ChevronRight,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface PoolWorkItem {
  assignment_id: string;
  work_order_id: string;
  work_order_number: string;
  product_name: string;
  customer_name: string;
  process_id: string;
  process_code: string;
  process_name: string;
  priority: string;
  due_date: string | null;
  pool_available_at: string | null;
  previous_process_completed_at: string | null;
  mold_name: string | null;
  estimated_duration: number | null;
}

interface PoolSummary {
  process_code: string;
  process_name: string;
  pending_count: number;
  min_skill_required: string | null;
  oldest_available: string | null;
}

const priorityConfig: Record<string, { label: string; color: string; order: number }> = {
  urgent: { label: "Urgente", color: "bg-red-500", order: 1 },
  high: { label: "Alta", color: "bg-orange-500", order: 2 },
  normal: { label: "Normal", color: "bg-blue-500", order: 3 },
  low: { label: "Baja", color: "bg-gray-500", order: 4 },
};

const skillLevelConfig: Record<string, { label: string; color: string }> = {
  A: { label: "Experto", color: "bg-emerald-500" },
  B: { label: "Intermedio", color: "bg-amber-500" },
  C: { label: "Básico", color: "bg-slate-500" },
};

export default function WorkPool() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [processFilter, setProcessFilter] = useState<string>("all");
  const [confirmDialog, setConfirmDialog] = useState<PoolWorkItem | null>(null);

  // Fetch operator's skill level
  const { data: operatorProfile } = useQuery({
    queryKey: ["operator-profile", user?.id],
    queryFn: async () => {
      const { data, error } = await api
        .from("profiles")
        .select("id, full_name, skill_level")
        .eq("id", user?.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch available work for operator
  const {
    data: availableWork,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["work-pool", user?.id],
    queryFn: async () => {
      const { data, error } = await api.rpc("get_available_work_for_operator", {
        p_operator_id: user?.id,
        p_limit: 100,
      });
      if (error) throw error;
      return data as PoolWorkItem[];
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch pool summary
  const { data: poolSummary } = useQuery({
    queryKey: ["work-pool-summary"],
    queryFn: async () => {
      const { data, error } = await api.rpc("get_work_pool_summary");
      if (error) throw error;
      return data as PoolSummary[];
    },
    refetchInterval: 30000,
  });

  // Claim work mutation
  const claimMutation = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { data, error } = await api.rpc("claim_work_from_pool", {
        p_assignment_id: assignmentId,
        p_operator_id: user?.id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (result: any) => {
      if (result.success) {
        toast.success("¡Trabajo asignado!", {
          description: "El trabajo ha sido asignado a ti",
        });
        queryClient.invalidateQueries({ queryKey: ["work-pool"] });
        queryClient.invalidateQueries({ queryKey: ["work-pool-summary"] });
        setConfirmDialog(null);
      } else {
        toast.error("Error al tomar trabajo", {
          description: result.error,
        });
      }
    },
    onError: (error: any) => {
      toast.error("Error", {
        description: error.message || "No se pudo tomar el trabajo",
      });
    },
  });

  // Filter work items
  const filteredWork = availableWork?.filter((item) => {
    const matchesSearch =
      searchTerm === "" ||
      item.work_order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesProcess =
      processFilter === "all" || item.process_code === processFilter;

    return matchesSearch && matchesProcess;
  });

  // Get unique processes for filter
  const uniqueProcesses = Array.from(
    new Set(availableWork?.map((item) => item.process_code) || [])
  ).map((code) => {
    const item = availableWork?.find((w) => w.process_code === code);
    return { code, name: item?.process_name || code };
  });

  const handleClaimWork = (item: PoolWorkItem) => {
    setConfirmDialog(item);
  };

  const confirmClaim = () => {
    if (confirmDialog) {
      claimMutation.mutate(confirmDialog.assignment_id);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Cola de Trabajo</h1>
            <p className="text-muted-foreground">
              Trabajos disponibles para tu nivel de habilidad
              {operatorProfile?.skill_level && (
                <Badge
                  className={cn(
                    "ml-2",
                    skillLevelConfig[operatorProfile.skill_level]?.color
                  )}
                >
                  Nivel {operatorProfile.skill_level} -{" "}
                  {skillLevelConfig[operatorProfile.skill_level]?.label}
                </Badge>
              )}
            </p>
          </div>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Pool Summary Cards */}
        {poolSummary && poolSummary.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {poolSummary.map((summary) => (
              <Card
                key={summary.process_code}
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md",
                  processFilter === summary.process_code && "ring-2 ring-primary"
                )}
                onClick={() =>
                  setProcessFilter(
                    processFilter === summary.process_code
                      ? "all"
                      : summary.process_code
                  )
                }
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold">
                      {summary.pending_count}
                    </span>
                    {summary.min_skill_required && (
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs",
                          skillLevelConfig[summary.min_skill_required]?.color,
                          "text-white"
                        )}
                      >
                        {summary.min_skill_required}+
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {summary.process_name}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por OT, producto o cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={processFilter} onValueChange={setProcessFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar proceso" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los procesos</SelectItem>
              {uniqueProcesses.map((proc) => (
                <SelectItem key={proc.code} value={proc.code}>
                  {proc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Work Items List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredWork && filteredWork.length > 0 ? (
          <div className="grid gap-4">
            {filteredWork.map((item) => (
              <Card
                key={item.assignment_id}
                className="hover:shadow-md transition-shadow"
              >
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Left: Order Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          className={cn(
                            priorityConfig[item.priority]?.color || "bg-blue-500"
                          )}
                        >
                          {priorityConfig[item.priority]?.label || item.priority}
                        </Badge>
                        <span className="font-mono font-semibold">
                          {item.work_order_number}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {item.product_name}
                      </p>
                      {item.customer_name && (
                        <p className="text-xs text-muted-foreground">
                          {item.customer_name}
                        </p>
                      )}
                    </div>

                    {/* Center: Process & Details */}
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <Badge variant="outline" className="font-medium">
                        {item.process_name}
                      </Badge>

                      {item.mold_name && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Package className="w-3 h-3" />
                          <span className="text-xs">{item.mold_name}</span>
                        </div>
                      )}

                      {item.due_date && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span className="text-xs">
                            {format(new Date(item.due_date), "dd MMM", {
                              locale: es,
                            })}
                          </span>
                        </div>
                      )}

                      {item.estimated_duration && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span className="text-xs">
                            ~{item.estimated_duration} min
                          </span>
                        </div>
                      )}

                      {item.pool_available_at && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <span className="text-xs">
                            Disponible{" "}
                            {format(
                              new Date(item.pool_available_at),
                              "dd/MM HH:mm",
                              { locale: es }
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Right: Action Button */}
                    <div className="flex justify-end">
                      <Button
                        onClick={() => handleClaimWork(item)}
                        disabled={claimMutation.isPending}
                        className="gap-2"
                      >
                        <Hand className="w-4 h-4" />
                        Tomar
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-medium mb-2">
                No hay trabajos disponibles
              </h3>
              <p className="text-muted-foreground">
                {processFilter !== "all"
                  ? "No hay trabajos pendientes para este proceso"
                  : "Todos los trabajos han sido asignados o están en progreso"}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Confirm Dialog */}
        <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Hand className="w-5 h-5" />
                Confirmar asignación
              </DialogTitle>
              <DialogDescription>
                ¿Deseas tomar este trabajo? Se te asignará y podrás comenzar
                inmediatamente.
              </DialogDescription>
            </DialogHeader>

            {confirmDialog && (
              <div className="space-y-3 py-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Orden:</span>
                  <span className="font-mono font-medium">
                    {confirmDialog.work_order_number}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Producto:</span>
                  <span>{confirmDialog.product_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Proceso:</span>
                  <Badge variant="outline">{confirmDialog.process_name}</Badge>
                </div>
                {confirmDialog.mold_name && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Molde:</span>
                    <span>{confirmDialog.mold_name}</span>
                  </div>
                )}
                {confirmDialog.estimated_duration && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Duración estimada:
                    </span>
                    <span>~{confirmDialog.estimated_duration} minutos</span>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog(null)}>
                Cancelar
              </Button>
              <Button
                onClick={confirmClaim}
                disabled={claimMutation.isPending}
              >
                {claimMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Asignando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Confirmar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
